import React from "react";
import { Helmet } from "react-helmet-async";
import { 
  Search, 
  Filter, 
  TrendingUp, 
  AlertCircle, 
  AlertTriangle,
  Zap, 
  Trees,
  ShieldCheck,
  ChevronRight,
  ArrowUpRight,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Info,
  Skull,
  Activity,
  ShieldAlert,
  HelpCircle,
  LayoutGrid,
  Rows,
  AlignJustify,
  GripHorizontal,
  SlidersHorizontal,
  MapPin,
  Calendar,
  Wind,
  Flame,
  Building2,
  Globe,
  Clock,
  Radar,
  List,
  AlignCenter
} from "lucide-react";
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle 
} from "./ui/card";
import { Skeleton } from "./ui/skeleton";
import { Input } from "./ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "./ui/tooltip";
import { Progress } from "./ui/progress";
import { api } from "../lib/api";
import { Company } from "../types";
import { cn } from "../lib/utils";

import { CompanyLogo } from "./CompanyLogo";

type SortField = "companyName" | "evilScore" | "totalEmissions" | "violations" | "deforestationHectares" | "region" | "sector" | "co2" | "methane" | "esgScore";
type SortOrder = "asc" | "desc";
type ViewMode = "list-compact" | "list-standard" | "list-detailed" | "grid";

const getCompanyBestSlug = (c: { companyName: string; slug: string }) => {
  return c.companyName
    .toLowerCase()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .trim() || c.slug;
};

export function Dashboard({ 
  onSelectCompany, 
  search, 
  setSearch, 
  stats: initialStats,
  initialCompanies,
  isLoading: isGlobalLoading
}: { 
  onSelectCompany: (c: Company | number) => void, 
  search: string, 
  setSearch: (s: string) => void, 
  stats?: any,
  initialCompanies?: Company[],
  isLoading?: boolean
}) {
  const [rawCompanies, setRawCompanies] = React.useState<Company[]>(initialCompanies || []);
  const [stats, setStats] = React.useState<any>(initialStats || null);
  const [loading, setLoading] = React.useState(!initialCompanies?.length);
  const [error, setError] = React.useState<string | null>(null);

  const [sector, setSector] = React.useState("all");
  const [region, setRegion] = React.useState("all");
  const [sortField, setSortField] = React.useState<SortField>("companyName");
  const [sortOrder, setSortOrder] = React.useState<SortOrder>("asc");
  const [pageSize, setPageSize] = React.useState(25);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [viewMode, setViewMode] = React.useState<ViewMode>("list-standard");
  const [showAdvancedFilters, setShowAdvancedFilters] = React.useState(false);
  const [minEvilScore, setMinEvilScore] = React.useState(0);
  const [minEmissions, setMinEmissions] = React.useState(0);

  const fetchDataRef = React.useRef(0);

  const companies = React.useMemo(() => {
    return rawCompanies.filter(c => 
      c.evilScore >= minEvilScore && 
      (c.totalEmissions || 0) >= minEmissions
    );
  }, [rawCompanies, minEvilScore, minEmissions]);

  // Sync with global loading state
  React.useEffect(() => {
    if (isGlobalLoading !== undefined) {
      setLoading(isGlobalLoading);
    }
  }, [isGlobalLoading]);

  // Sync with initial companies
  React.useEffect(() => {
    if (initialCompanies && initialCompanies.length > 0) {
      setRawCompanies(initialCompanies);
      setLoading(false);
    } else if (initialCompanies && initialCompanies.length === 0 && !isGlobalLoading) {
      // If App finished loading but we have no companies, stop the spinner
      setLoading(false);
    }
  }, [initialCompanies, isGlobalLoading]);

  // Sync with initial stats prop
  React.useEffect(() => {
    if (initialStats) {
      setStats(initialStats);
    }
  }, [initialStats]);

  const getDashboardTitle = () => {
    if (search) return `Search Results for "${search}" | Biofail Index`;
    if (sector !== "all") return `${sector} Impact Index | Biofail Index`;
    if (region !== "all") return `Corporate Impact in ${region} | Biofail Index`;
    return "Corporate Destruction Index | Global Emissions & Violation Database";
  };

  const getDashboardDesc = () => {
    if (sector !== "all") return `Exploration of ${sector} sector corporations and their ecological footprint, emissions, and legal violations.`;
    return "Browse the comprehensive database of global corporate environmental impact, forest loss, and resource extraction metrics.";
  };

  React.useEffect(() => {
    setCurrentPage(1);
  }, [search, sector, region, sortField, sortOrder, minEvilScore, minEmissions]);

  React.useEffect(() => {
    const fetchData = async () => {
      // Avoid duplicate fetch if initialCompanies already match the primary filters or if app is globally loading
      if (isGlobalLoading && !search && sector === "all" && region === "all") {
        return;
      }
      if (!search && sector === "all" && region === "all" && initialCompanies && initialCompanies.length > 0) {
        return;
      }

      const requestId = ++fetchDataRef.current;
      setLoading(true);
      setError(null);

      try {
        const companiesData = await api.getCompanies({ 
          search: search || undefined, 
          sector: sector === "all" ? undefined : sector,
          region: region === "all" ? undefined : region
        });
        
        if (requestId !== fetchDataRef.current) return;

        setRawCompanies(companiesData);
        
        // Ensure we have some stats to show
        if (!stats) {
          try {
            const statsData = await api.getStats();
            if (statsData && statsData.totalCompanies > 0) {
              setStats(statsData);
            }
          } catch (e) {
            console.error("Dashboard: Stats fetch failed", e);
          }
        }
      } catch (err) {
        if (requestId !== fetchDataRef.current) return;
        console.error("Dashboard: Failed to fetch data:", err);
        const errorMessage = err instanceof Error ? err.message : "Unknown error";
        const urlContext = `Target: /api/companies (Origin: ${typeof window !== "undefined" ? window.location.origin : "SSR"})`;
        setError(`DATABASE_SYNC_FAILURE: ${errorMessage}. ${urlContext}. The intelligence registry (port 3000) may be unreachable or busy.`);
      } finally {
        if (requestId === fetchDataRef.current) {
          setLoading(false);
        }
      }
    };

    fetchData();
  }, [search, sector, region]);

  const sortedCompanies = React.useMemo(() => {
    const sorted = [...companies].sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];
      
      if (typeof aVal === "string" && typeof bVal === "string") {
        return sortOrder === "asc" 
          ? aVal.localeCompare(bVal) 
          : bVal.localeCompare(aVal);
      }
      
      if (typeof aVal === "number" && typeof bVal === "number") {
        return sortOrder === "asc" ? aVal - bVal : bVal - aVal;
      }
      
      return 0;
    });
    return sorted;
  }, [companies, sortField, sortOrder]);

  const paginatedCompanies = React.useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return sortedCompanies.slice(start, start + pageSize);
  }, [sortedCompanies, currentPage, pageSize]);

  const totalPages = Math.ceil(sortedCompanies.length / pageSize);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("desc");
    }
  };

  const sectors = ["Oil & Gas", "Mining", "Utilities", "Transport", "Chemicals", "Agriculture", "Manufacturing", "Automotive", "Technology", "Finance", "Consumer Goods", "Retail", "Pharmaceuticals"];
  const regions = ["North America", "Europe", "Asia", "South America", "Middle East", "Oceania", "Africa"];

  // Top 4 for Infographic
  const topFour = React.useMemo(() => {
    if (!Array.isArray(companies) || companies.length === 0) return [];
    return [...companies]
      .sort((a, b) => (b.evilScore || 0) - (a.evilScore || 0))
      .slice(0, 4);
  }, [companies]);

  const datasetSchema = {
    "@context": "https://schema.org",
    "@type": "Dataset",
    "name": "Biofail Corporate Environmental Impact Index",
    "description": "Global database tracking corporate carbon emissions, deforestation, and regulatory violations.",
    "url": typeof window !== "undefined" ? window.location.origin : "",
    "keywords": "carbon emissions, deforestation tracking, ESG violations, corporate accountability, environmental data",
    "creator": {
      "@type": "Organization",
      "name": "Biofail Index"
    },
    "variableMeasured": [
      "Carbon Emissions (CO2e)",
      "Deforestation (Hectares)",
      "Regulatory Violations",
      "Evil Score"
    ]
  };

  if (error) {
    const isCustomDomain = typeof window !== "undefined" && !window.location.hostname.includes("run.app") && !window.location.hostname.includes("localhost");
    
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] p-8 text-center bg-stone-50">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-6 border border-red-200">
          <AlertTriangle className="w-8 h-8 text-red-600" />
        </div>
        <h2 className="text-2xl font-bold text-stone-900 mb-2">REGISTRY_SYNC_FAILURE</h2>
        <div className="text-stone-500 max-w-2xl mx-auto mb-8 font-medium leading-relaxed space-y-4">
          <p>
            The intelligence node (AI_S3001) encountered a synchronization error while attempting to fetch the entity registry. 
          </p>
          {isCustomDomain && (
            <div className="p-6 bg-amber-50 border border-amber-200 text-stone-800 text-sm rounded-lg text-left shadow-sm space-y-4">
              <div className="flex items-center gap-2 text-amber-800 font-bold">
                <Globe className="w-5 h-5" />
                <span>Custom Domain Routing Misconfiguration Detected</span>
              </div>
              <p className="text-xs text-stone-600 leading-relaxed">
                You are accessing the application via the custom domain <b>{window.location.hostname}</b>. The browser attempted to fetch the database from the relative address <code>/api/companies</code>, which returned a <b>444/404 Connection Error</b>. 
              </p>
              <div className="space-y-3 pt-2 text-xs">
                <div className="border-l-2 border-amber-500 pl-3">
                  <p className="font-bold text-stone-900">Why this happens:</p>
                  <p className="text-stone-600 font-normal">
                    This domain is likely configured on a <b>static web host hosting only the front-end bundle</b> (like Vercel SPA or GitHub Pages). Since there is no Node/Express server running on your custom domain host, relative <code>/api</code> endpoints do not exist.
                  </p>
                </div>
                <div className="border-l-2 border-emerald-500 pl-3">
                  <p className="font-bold text-stone-900">Action 1: Use the Official Shared Link (Instant Fix)</p>
                  <p className="text-stone-600 font-normal">
                    Access the app directly via the official secure Cloud Run environment which has the active database node:
                    <br />
                    <a 
                      href="https://ais-pre-zymebnmkg6alofwx7vkwzx-39482390293.us-east1.run.app" 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="text-destructive font-bold hover:underline break-all mt-1 inline-block"
                    >
                      https://ais-pre-zymebnmkg6alofwx7vkwzx-39482390293.us-east1.run.app
                    </a>
                  </p>
                </div>
                <div className="border-l-2 border-indigo-500 pl-3">
                  <p className="font-bold text-stone-900">Action 2: Real Full-Stack Domain Mapping</p>
                  <p className="text-stone-600 font-normal">
                    To make <code>{window.location.hostname}</code> work, map your DNS directly to the Cloud Run container instead of a static host. This permits the Express backend in <code>server.ts</code> to serve both the frontend files and API requests together under the same origin.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="p-4 bg-white border border-red-200 text-red-600 text-xs font-mono mb-8 max-w-2xl overflow-auto w-full text-left">
          <div className="mb-2 pb-2 border-b border-red-100 font-bold uppercase">Trace Log:</div>
          {error}
        </div>
        <div className="flex flex-wrap items-center justify-center gap-4">
          <Button 
            variant="destructive"
            onClick={() => window.location.reload()}
            className="font-bold px-8 h-12 uppercase tracking-wide cursor-pointer"
          >
            Force Registry Re-Sync
          </Button>
          <Button 
            variant="outline"
            asChild
            className="font-bold px-8 h-12 uppercase tracking-wide border-stone-300"
          >
            <a href="/api-debug" target="_blank" rel="noopener noreferrer">Registry Debug Info</a>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col pb-12 bg-stone-50 min-h-screen">
      <Helmet>
        <title>{getDashboardTitle()}</title>
        <meta name="description" content={getDashboardDesc()} />
      </Helmet>
      
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 mt-6 space-y-4 relative">
        {/* Core editorial title for perfect semantic crawler structure & branding */}
        <div className="flex flex-col gap-1.5 pb-2">
          <h1 className="text-2xl md:text-3.5xl font-extrabold tracking-tight text-stone-950 font-sans">
            Corporate Environmental Impact Index
          </h1>
          <p className="text-sm font-bold text-stone-500 leading-none">
            Radical transparency and ecological tracking database of high-emission operators.
          </p>
        </div>

        {/* Intelligence Metrics status bars */}
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 py-3 px-4 border border-stone-300 rounded bg-white shadow-sm">
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-stone-500">Entities:</span>
            <span className="text-sm font-bold text-stone-900">{(stats?.totalCompanies || 0).toLocaleString()}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-red-600">Threats:</span>
            <span className="text-sm font-bold text-red-600">{(stats?.criticalThreats || 0).toLocaleString()}</span>
          </div>
          <div className="flex items-center gap-2 border-l pl-6 border-stone-200">
            <span className="text-sm font-bold text-stone-500">Violations:</span>
            <span className="text-sm font-bold text-stone-900">{(stats?.totalViolations || 0).toLocaleString()}</span>
          </div>
          <div className="flex items-center gap-2 border-l pl-6 border-stone-200">
            <span className="text-sm font-bold text-stone-500">Deforestation:</span>
            <span className="text-sm font-bold text-stone-900">{stats?.totalDeforestation ? (stats.totalDeforestation / 1000000).toFixed(1) : "0"}M Ha</span>
          </div>
        </div>

        {/* Search & Filter Section */}
        <Card className="w-full border border-stone-300 shadow-sm bg-white overflow-hidden rounded-sm">
          <CardHeader className="p-0 border-b border-stone-200">
            <div className="bs-header flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <div className="space-y-0.5">
                <CardTitle className="text-sm font-bold text-stone-900 m-0">
                  Data Registry
                </CardTitle>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <div className="flex items-center gap-1 bg-white p-1 rounded border h-8 px-1 shadow-sm">
                  <div className="flex items-center gap-1 border-r pr-1 mr-1">
                    <Tooltip>
                      <TooltipTrigger>
                        <Button 
                          variant={viewMode === "grid" ? "secondary" : "ghost"} 
                          size="icon" 
                          className="h-6 w-6" 
                          onClick={() => setViewMode("grid")}
                        >
                          <LayoutGrid className="h-3.5 w-3.5" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Grid View</TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger>
                        <Button 
                          variant={viewMode === "list-detailed" ? "secondary" : "ghost"} 
                          size="icon" 
                          className="h-6 w-6" 
                          onClick={() => setViewMode("list-detailed")}
                        >
                          <Rows className="h-3.5 w-3.5" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Detailed List</TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger>
                        <Button 
                          variant={viewMode === "list-standard" ? "secondary" : "ghost"} 
                          size="icon" 
                          className="h-6 w-6" 
                          onClick={() => setViewMode("list-standard")}
                        >
                          <AlignJustify className="h-3.5 w-3.5" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Standard List</TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger>
                        <Button 
                          variant={viewMode === "list-compact" ? "secondary" : "ghost"} 
                          size="icon" 
                          className="h-6 w-6" 
                          onClick={() => setViewMode("list-compact")}
                        >
                          <GripHorizontal className="h-3.5 w-3.5" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Compact List</TooltipContent>
                    </Tooltip>
                  </div>

                  <div className="flex items-center gap-2">
                    <Select value={sector} onValueChange={setSector}>
                      <SelectTrigger className="w-[180px] h-9 bg-white border border-stone-200 text-sm font-bold px-3 shadow-sm">
                        <SelectValue placeholder="Sectors" />
                      </SelectTrigger>
                      <SelectContent className="z-[100] bg-white border shadow-2xl min-w-[180px]">
                        <SelectItem value="all" className="text-sm">All Sectors</SelectItem>
                        {sectors.map(s => <SelectItem key={s} value={s} className="text-sm">{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  
                    <Select value={region} onValueChange={setRegion}>
                      <SelectTrigger className="w-[180px] h-9 bg-white border border-stone-200 text-sm font-bold px-3 shadow-sm">
                        <SelectValue placeholder="Regions" />
                      </SelectTrigger>
                      <SelectContent className="z-[100] bg-white border shadow-2xl min-w-[180px]">
                        <SelectItem value="all" className="text-sm">All Regions</SelectItem>
                        {regions.map(r => <SelectItem key={r} value={r} className="text-sm">{r}</SelectItem>)}
                      </SelectContent>
                    </Select>

                    <Button 
                      variant={showAdvancedFilters ? "secondary" : "ghost"} 
                      size="sm" 
                      className="h-9 gap-2 text-sm font-bold"
                      onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                    >
                      <SlidersHorizontal className="w-4 h-4" />
                      Filters
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {showAdvancedFilters && (
              <div className="overflow-hidden border-b bg-muted/10 transition-all duration-300">
                <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <label htmlFor="filter-index-score" className="text-sm font-bold text-muted-foreground">Index Score</label>
                      <span className="text-sm font-bold">{minEvilScore}</span>
                    </div>
                    <Input 
                      id="filter-index-score"
                      type="range" 
                      min="0" 
                      max="100" 
                      value={minEvilScore} 
                      onChange={(e) => setMinEvilScore(parseInt(e.target.value))}
                      className="h-4 accent-stone-900"
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <label htmlFor="filter-emissions" className="text-sm font-bold text-muted-foreground">Emissions (MT)</label>
                      <span className="text-sm font-bold">{minEmissions}</span>
                    </div>
                    <Input 
                      id="filter-emissions"
                      type="range" 
                      min="0" 
                      max="200" 
                      value={minEmissions} 
                      onChange={(e) => setMinEmissions(parseInt(e.target.value))}
                      className="h-4 accent-stone-900"
                    />
                  </div>
                  <div className="flex items-end">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-sm font-bold h-9"
                      onClick={() => {
                        setMinEvilScore(0);
                        setMinEmissions(0);
                        setSector("all");
                        setRegion("all");
                      }}
                    >
                      Reset All Filters
                    </Button>
                  </div>
                </div>
              </div>
            )}

            <div className="">
              {error ? (
                <div className="p-12 text-center">
                  <div className="max-w-md mx-auto bg-white border border-stone-200 p-8 shadow-sm rounded">
                    <div className="w-16 h-16 bg-red-50 text-red-600 rounded-full flex items-center justify-center mx-auto mb-4 border border-red-100">
                      <AlertTriangle className="h-8 w-8" />
                    </div>
                    <h3 className="text-lg font-bold text-stone-900 mb-2">Registry Connection Failed</h3>
                    <p className="text-sm text-stone-500 mb-6 font-medium">
                      The intelligence node (AI_S3001) returned a 404 error while attempting to sync the entity registry. 
                      This typically indicates a synchronization lag or a database version mismatch.
                    </p>
                    <div className="space-y-3">
                      <Button onClick={() => window.location.reload()} className="w-full bg-stone-900 font-bold h-11">
                        Reconnect to Registry
                      </Button>
                      <a href="/diagnostic" target="_blank" rel="noopener noreferrer" className="block text-sm font-bold text-stone-500 hover:text-stone-900 transition-colors">
                        Run Node Diagnostics
                      </a>
                    </div>
                  </div>
                </div>
              ) : viewMode === "grid" ? (
                <div className="p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 bg-muted/5">
                  {loading ? (
                    Array.from({ length: 8 }).map((_, i) => (
                      <Card key={i} className="h-full border border-stone-200 shadow-sm overflow-hidden bg-white">
                        <div className="p-3 flex flex-col h-full space-y-4">
                          <div className="flex justify-between items-start">
                            <Skeleton className="w-10 h-10 rounded-full bg-stone-250" />
                            <Skeleton className="w-8 h-8 rounded-sm bg-stone-250" />
                          </div>
                          
                          <div className="space-y-2">
                            <Skeleton className="h-4 w-3/4 bg-stone-250" />
                            <Skeleton className="h-3 w-1/2 bg-stone-200" />
                          </div>

                          <div className="mt-auto space-y-3">
                            <div className="grid grid-cols-2 gap-1.5">
                              <div className="bg-stone-50/50 p-1.5 border border-stone-100 rounded-none">
                                <Skeleton className="h-2.5 w-8 bg-stone-200 mb-1.5" />
                                <Skeleton className="h-3.5 w-12 bg-stone-250" />
                              </div>
                              <div className="bg-stone-50/50 p-1.5 border border-stone-100 rounded-none">
                                <Skeleton className="h-2.5 w-8 bg-stone-200 mb-1.5" />
                                <Skeleton className="h-3.5 w-12 bg-stone-250" />
                              </div>
                            </div>
                            
                            <div className="flex items-center justify-between pt-2 border-t border-stone-100">
                              <Skeleton className="h-3.5 w-12 bg-stone-200" />
                              <Skeleton className="h-4 w-16 bg-stone-250" />
                            </div>
                          </div>
                        </div>
                      </Card>
                    ))
                  ) : (
                    paginatedCompanies.map((company, i) => (
                      <a
                        key={company.id}
                        href={`/${getCompanyBestSlug(company)}`}
                        onClick={(e) => {
                          e.preventDefault();
                          onSelectCompany(company);
                        }}
                        aria-label={`View environmental dossier for ${company.companyName}. Sector: ${company.sector}, Region: ${company.region}, Biofail Evil Score: ${company.evilScore ?? "--"}/100. Click to view.`}
                        className="group cursor-pointer block relative focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-500 rounded-sm"
                      >
                      <Card className="h-full border border-stone-300 hover:border-stone-500 transition-all shadow-sm overflow-hidden bg-white">
                        <div className="p-3 flex flex-col h-full relative z-20">
                          <div className="flex justify-between items-start mb-3">
                            <CompanyLogo 
                              companyName={company.companyName} 
                              logoUrl={company.logoUrl} 
                              domain={company.domain}
                              sector={company.sector}
                              size="md"
                            />
                            <div 
                              className={cn(
                                "font-bold w-8 h-8 rounded-sm flex items-center justify-center text-xs shadow-sm border",
                                (company.evilScore || 0) >= 90 ? "bg-stone-900 text-white border-stone-900" : 
                                "bg-white text-stone-700 border-stone-300"
                              )}
                            >
                              {company.evilScore ?? "--"}
                            </div>
                          </div>
                          
                          <div className="space-y-0.5 mb-3 flex items-center justify-between gap-2 overflow-hidden">
                            <div className="flex flex-col min-w-0">
                              <div className="flex items-center gap-1.5 min-w-0">
                                <h3 className="font-bold text-sm leading-tight text-stone-900 truncate">{company.companyName}</h3>
                                <div className="flex items-center gap-0.5 shrink-0">
                                  {company.tags.includes("Verified") && <ShieldCheck className="w-3 h-3 text-emerald-600" />}
                                  {(company.evilScore || 0) >= 98 && <ShieldAlert className="w-3 h-3 text-red-600" />}
                                </div>
                              </div>
                              <p className="text-xs text-stone-600 font-bold truncate">{company.sector} • {company.region}</p>
                            </div>
                          </div>

                          <div className="mt-auto space-y-2">
                            <div className="grid grid-cols-2 gap-1.5">
                              <div className="bg-stone-50 p-1.5 border border-stone-200">
                                <p className="text-xs font-bold text-stone-600 leading-none mb-1">CO2</p>
                                <p className="text-xs font-bold text-stone-800">{company.co2 || ((company.totalEmissions || 0) * 0.8).toFixed(1)}</p>
                              </div>
                              <div className="bg-stone-50 p-1.5 border border-stone-200">
                                <p className="text-xs font-bold text-stone-600 leading-none mb-1">CH4</p>
                                <p className="text-xs font-bold text-stone-800">{company.methane || ((company.totalEmissions || 0) * 0.2 * 1000).toFixed(0)}</p>
                              </div>
                            </div>
                            
                            <div className="flex items-center justify-between pt-1 border-t border-stone-100">
                              <div className="flex gap-1">
                                <span className="text-xs font-bold text-stone-600">{company.headquarters || "Global"}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <span className="text-sm font-bold text-stone-900">{(company.totalEmissions || 0).toLocaleString()}Mt</span>
                                <ChevronRight className="w-3 h-3 text-stone-500" />
                              </div>
                            </div>
                          </div>
                        </div>
                      </Card>
                    </a>
                    ))
                  )}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table className="w-full min-w-[1000px]">
                  <TableHeader>
                    <TableRow className="hover:bg-transparent border-b bg-stone-50/50">
                      <SortableHeader 
                        label="Entity" 
                        field="companyName" 
                        currentField={sortField} 
                        order={sortOrder} 
                        onSort={handleSort}
                        tooltip="The legal name of the corporate entity under investigation."
                        className="border-r border-stone-200 pl-4"
                      />
                      <SortableHeader 
                        label="Score" 
                        field="evilScore" 
                        currentField={sortField} 
                        order={sortOrder} 
                        onSort={handleSort}
                        className="text-center w-[70px]"
                        tooltip="Proprietary metric combining emissions and violations."
                      />
                      <SortableHeader 
                        label="Total Em." 
                        field="totalEmissions" 
                        currentField={sortField} 
                        order={sortOrder} 
                        onSort={handleSort}
                        className="text-right w-[100px]"
                        tooltip="Total CO2e in MT."
                      />
                      <SortableHeader 
                        label="CO2" 
                        field="co2" 
                        currentField={sortField} 
                        order={sortOrder} 
                        onSort={handleSort}
                        className="text-right w-[100px] border-r border-stone-200"
                      />
                      <SortableHeader 
                        label="CH4" 
                        field="methane" 
                        currentField={sortField} 
                        order={sortOrder} 
                        onSort={handleSort}
                        className="text-right w-[100px] border-r border-stone-200"
                      />
                      <SortableHeader 
                        label="ESG" 
                        field="esgScore" 
                        currentField={sortField} 
                        order={sortOrder} 
                        onSort={handleSort}
                        className="text-right w-[80px] border-r border-stone-200"
                      />
                      <SortableHeader 
                        label="Viol." 
                        field="violations" 
                        currentField={sortField} 
                        order={sortOrder} 
                        onSort={handleSort}
                        className="text-right w-[100px] border-r border-stone-200"
                      />
                      <SortableHeader 
                        label="Impact (Ha)" 
                        field="deforestationHectares" 
                        currentField={sortField} 
                        order={sortOrder} 
                        onSort={handleSort}
                        className="text-right w-[120px]"
                      />
                      <TableHead className="w-[50px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      Array.from({ length: 8 }).map((_, i) => (
                        <TableRow key={i} className="border-b border-stone-100 bg-white">
                          {/* Entity Column */}
                          <TableCell className={cn("pl-6 pr-4", viewMode === "list-compact" ? "py-2" : viewMode === "list-detailed" ? "py-5" : "py-3")}>
                            <div className="flex items-center gap-3">
                              <Skeleton className={cn("rounded-full bg-stone-200 shrink-0", 
                                viewMode === "list-compact" ? "w-6 h-6" : viewMode === "list-detailed" ? "w-10 h-10" : "w-8 h-8"
                              )} />
                              <div className="space-y-1.5 w-full max-w-[160px]">
                                <Skeleton className="h-4 w-full bg-stone-250" />
                                {viewMode === "list-detailed" && <Skeleton className="h-3 w-2/3 bg-stone-200" />}
                              </div>
                            </div>
                          </TableCell>
                          
                          {/* Score Column */}
                          <TableCell className="text-center px-2 py-3">
                            <div className="flex justify-center">
                              <Skeleton className={cn("rounded-sm bg-stone-250", 
                                viewMode === "list-compact" ? "w-5 h-5" : "w-8 h-8"
                              )} />
                            </div>
                          </TableCell>
                          
                          {/* Total Em. Column */}
                          <TableCell className="text-right px-4 py-3">
                            <div className="flex justify-end">
                              <Skeleton className="h-4 w-12 bg-stone-250" />
                            </div>
                          </TableCell>
                          
                          {/* CO2 Column */}
                          <TableCell className="text-right px-4 py-3">
                            <div className="flex justify-end">
                              <Skeleton className="h-4 w-12 bg-stone-200" />
                            </div>
                          </TableCell>
                          
                          {/* CH4 Column */}
                          <TableCell className="text-right px-5 py-3">
                            <div className="flex justify-end">
                              <Skeleton className="h-4 w-16 bg-stone-200" />
                            </div>
                          </TableCell>
                          
                          {/* ESG Column */}
                          <TableCell className="text-right px-5 py-3">
                            <div className="flex justify-end">
                              <Skeleton className="h-4 w-8 bg-stone-200" />
                            </div>
                          </TableCell>
                          
                          {/* Viol. Column */}
                          <TableCell className="text-right px-5 py-3">
                            <div className="flex justify-end">
                              <Skeleton className="h-4 w-10 bg-stone-250" />
                            </div>
                          </TableCell>
                          
                          {/* Impact (Ha) Column */}
                          <TableCell className="text-right px-5 py-3">
                            <div className="flex justify-end">
                              <Skeleton className="h-4 w-14 bg-stone-250" />
                            </div>
                          </TableCell>
                          
                          {/* Chevron Arrow Column */}
                          <TableCell className="pl-2 pr-8 text-right py-3">
                            <div className="flex justify-end pr-1">
                              <Skeleton className="w-5 h-5 rounded-sm bg-stone-200" />
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : error ? (
                      <TableRow>
                        <TableCell colSpan={9} className="h-40 text-center">
                          <div className="flex flex-col items-center justify-center gap-3">
                            <AlertCircle className="w-8 h-8 text-destructive/50" />
                            <p className="text-sm font-medium text-destructive">{error}</p>
                            <Button variant="outline" size="sm" onClick={() => window.location.reload()}>
                              Force Reload
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : (paginatedCompanies || []).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} className="h-40 text-center">
                          <div className="flex flex-col items-center justify-center gap-3">
                            <Radar className="w-8 h-8 text-stone-300" />
                            <div className="space-y-1">
                              <p className="text-sm font-bold text-stone-500">No Results Found</p>
                              <p className="text-sm text-stone-400">Your search or filter parameters did not return any matches.</p>
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : (
                      (paginatedCompanies || []).map((company, index) => (
                        <tr 
                          key={company.id} 
                          tabIndex={0}
                          role="button"
                          aria-label={`View dossier for ${company.companyName}. Sector: ${company.sector}, Region: ${company.region}, Evil Score: ${company.evilScore ?? "--"}/100. Press Enter or Space to open.`}
                          className="group cursor-pointer hover:bg-muted/50 transition-all border-b last:border-0 relative focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-500 focus-visible:bg-stone-50"
                          onClick={() => onSelectCompany(company)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              onSelectCompany(company);
                            }
                          }}
                        >
                           <TableCell className={cn("pl-6 pr-4", viewMode === "list-compact" ? "py-1" : viewMode === "list-detailed" ? "py-4" : "py-2")}>
                             <div className="flex items-center gap-3 min-w-0">
                               <CompanyLogo 
                                 companyName={company.companyName} 
                                 logoUrl={company.logoUrl} 
                                 domain={company.domain}
                                 sector={company.sector}
                                 size={viewMode === "list-compact" ? "xs" : viewMode === "list-detailed" ? "md" : "sm"}
                               />
                               <div className="flex items-center gap-2 min-w-0 flex-nowrap shrink">
                                 <span className={cn("font-bold text-stone-900 group-hover:text-black transition-colors truncate shrink min-w-0", viewMode === "list-compact" ? "text-xs" : "text-sm")}>
                                   {company.companyName}
                                 </span>
                               </div>
                             </div>
                           </TableCell>
                          <TableCell className={cn("text-center px-2 w-[40px]", viewMode === "list-compact" ? "py-1" : "py-1.5")}>
                            <div className="flex justify-center">
                                  <div 
                                    className={cn(
                                      "font-bold rounded-sm flex items-center justify-center border",
                                      viewMode === "list-compact" ? "w-5 h-5 text-[10px]" : "w-8 h-8 text-sm",
                                      (company.evilScore || 0) >= 90 ? "bg-stone-900 text-white border-stone-800" : 
                                      "bg-white text-stone-700 border-stone-300"
                                    )}
                                  >
                                    {company.evilScore !== undefined ? Math.round(company.evilScore) : "--"}
                                  </div>
                            </div>
                          </TableCell>
                          <TableCell className={cn("text-right font-medium px-4 w-[80px] group/cell", viewMode === "list-compact" ? "py-1 text-xs" : "py-1.5 text-base")}>
                            <div className="flex flex-col items-end">
                              <span className="text-foreground">{(company.totalEmissions || 0).toLocaleString()}</span>
                            </div>
                          </TableCell>
                          
                          <TableCell className={cn("text-right font-medium px-4 w-[80px] group/cell", viewMode === "list-compact" ? "py-1 text-xs" : "py-1.5 text-base")}>
                            <div className="flex flex-col items-end">
                              <span className="text-foreground">{(company.co2 || ((company.totalEmissions || 0) * 0.8)).toLocaleString()}</span>
                            </div>
                          </TableCell>
                          
                          <TableCell className={cn("text-right font-medium px-5 w-[100px] group/cell", viewMode === "list-compact" ? "py-1 text-xs" : "py-1.5 text-base")}>
                            <div className="flex flex-col items-end">
                              <span className="text-foreground">{(company.methane || ((company.totalEmissions || 0) * 0.2 * 1000)).toLocaleString()}</span>
                            </div>
                          </TableCell>
                          <TableCell className={cn("text-right font-medium px-5 w-[80px] group/cell", viewMode === "list-compact" ? "py-1 text-xs" : "py-1.5 text-base")}>
                            <div className="flex flex-col items-end">
                              <span className="text-foreground">{company.esgScore?.toLocaleString() || "--"}</span>
                            </div>
                          </TableCell>
                          <TableCell className={cn("text-right font-medium px-5 w-[100px] group/cell", viewMode === "list-compact" ? "py-1 text-xs" : "py-1.5 text-base")}>
                            <div className="flex flex-col items-end">
                              <span className="text-foreground">{(company.violations || 0).toLocaleString()}</span>
                            </div>
                          </TableCell>
                          <TableCell className={cn("text-right font-medium px-5 w-[120px] group/cell", viewMode === "list-compact" ? "py-1 text-xs" : "py-1.5 text-base")}>
                            <div className="flex flex-col items-end">
                              <span className="text-foreground">{(company.deforestationHectares || 0).toLocaleString()}</span>
                            </div>
                          </TableCell>
                        <TableCell className={cn("pl-2 pr-8 text-right", viewMode === "list-compact" ? "py-1" : "py-1.5")}>
                          <div className="flex justify-end pr-1">
                            <div className="w-5 h-5 rounded-sm bg-stone-50 border border-stone-200 flex items-center justify-center group-hover:bg-stone-100 group-hover:border-stone-400 transition-all duration-200">
                              <ChevronRight className="w-3 h-3 text-stone-400 group-hover:text-stone-900" />
                            </div>
                          </div>
                        </TableCell>
                        </tr>
                      ))
                    )}
                  </TableBody>
                  </Table>
                </div>
              )}
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="p-4 border-t bg-stone-50/50 flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="text-sm font-bold text-stone-500 order-2 sm:order-1">
                  Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, sortedCompanies.length)} of {sortedCompanies.length} entities
                </div>
                
                <div className="flex items-center gap-4 order-1 sm:order-2">
                  <div className="flex items-center gap-2 pr-4 border-r border-stone-200">
                    <span className="text-sm font-bold text-stone-500">Entries</span>
                    <Select value={pageSize.toString()} onValueChange={(v) => { setPageSize(parseInt(v)); setCurrentPage(1); }}>
                      <SelectTrigger className="w-[80px] h-9 bg-white border-stone-200 text-sm font-bold focus:ring-0 shadow-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[10, 25, 50, 100].map(size => (
                          <SelectItem key={size} value={size.toString()} className="text-sm">{size}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="h-9 font-bold text-sm"
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                    >
                      Prev
                    </Button>
                    <div className="flex items-center gap-1 px-4">
                      <span className="text-sm font-bold">{currentPage}</span>
                      <span className="text-sm text-stone-400">/</span>
                      <span className="text-sm font-bold">{totalPages}</span>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="h-9 font-bold text-sm"
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Popular Impact Reports - SEO Internal Linking */}
      <section className="py-12 border-t bg-white mt-8 overflow-hidden">
        <div className="container mx-auto px-4 sm:px-6 max-w-7xl relative">
          <div className="flex flex-col md:flex-row justify-between items-start gap-4 mb-8">
            <div className="space-y-1">
               <h2 className="text-xl font-bold text-stone-900 m-0">Active Corporate Index</h2>
               <p className="text-sm text-stone-500 font-semibold">Quick access list for primary corporate profiles currently under environmental audit.</p>
            </div>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 relative z-10">
            {companies.slice(0, 12).map((c) => (
              <a 
                key={c.id}
                href={`/${getCompanyBestSlug(c)}`}
                onClick={(e) => {
                  e.preventDefault();
                  onSelectCompany(c);
                }}
                className="group p-2.5 bg-stone-50 border border-stone-200 rounded-sm hover:border-stone-400 transition-all text-left block"
              >
                <div className="text-xs font-bold text-stone-600 mb-0.5 line-clamp-1">{c.sector}</div>
                <div className="font-bold text-stone-950 line-clamp-1 text-sm">{c.companyName}</div>
                <div className="text-xs text-stone-600 mt-1 flex items-center justify-between font-bold">
                  <span>Score: {c.evilScore}</span>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* SEO Hidden Footer - Ensures crawlers can find every company link */}
      <footer className="sr-only" style={{ position: 'absolute', width: '1px', height: '1px', padding: '0', margin: '-1px', overflow: 'hidden', clip: 'rect(0,0,0,0)', border: '0' }}>
        <h2>Complete ESG Intelligence Directory</h2>
        <ul>
          {companies.map(c => (
            <li key={c.id}>
              <a href={`/${getCompanyBestSlug(c)}`}>{c.companyName} ESG Report & Destruction Dossier</a>
            </li>
          ))}
        </ul>
      </footer>
    </div>
  );
}

function CompactStatCard({ title, value, color, icon: Icon }: { title: string; value: string | number; color: "red" | "blue" | "orange" | "green", icon: any }) {
  const colors: any = {
    blue: "border-blue-500/10 text-blue-700 bg-blue-50/50",
    red: "border-red-500/10 text-red-700 bg-red-50/50",
    orange: "border-orange-500/10 text-orange-700 bg-orange-50/50",
    green: "border-green-500/10 text-green-700 bg-green-50/50",
  };

  return (
    <div className={cn("p-3 border flex items-center gap-3 shadow-sm transition-all hover:shadow-md rounded h-full", colors[color])}>
      <div className="p-2 rounded-md bg-white/50 shadow-inner shrink-0">
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex flex-col min-w-0">
        <span className="text-sm font-bold text-stone-600 mb-0.5">{title}</span>
        <span className="text-lg md:text-xl font-bold tracking-tight truncate">{value}</span>
      </div>
    </div>
  );
}

function StatCard({ title, value, icon: Icon, description, color }: any) {
  const colors: any = {
    blue: "text-blue-600 bg-blue-50 border-blue-100",
    red: "text-red-600 bg-red-50 border-red-100",
    orange: "text-orange-600 bg-orange-50 border-orange-100",
    green: "text-green-600 bg-green-50 border-green-100",
  };

  return (
    <div className="transform hover:-translate-y-0.5 hover:scale-[1.01] transition-all duration-200">
      <Card className="overflow-hidden border-none shadow-md bg-background/80 backdrop-blur-sm">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <div className={cn("p-1.5 rounded-lg border shadow-sm", colors[color])}>
              <Icon className="w-4 h-4" />
            </div>
            <div className="text-xs font-bold text-stone-600">Sys Ok</div>
          </div>
          <div className="space-y-1">
            <p className="text-sm font-bold text-stone-600">{title}</p>
            <h3 className="text-2xl font-bold text-black tracking-tight">{value}</h3>
            <p className="text-sm text-stone-600 font-bold flex items-center gap-1">
              {description}
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function StatItem({ label, value, icon: Icon, color }: any) {
  return (
    <div className="bg-white p-4 border border-stone-200 flex flex-col gap-1 relative group hover:border-stone-400 transition-colors rounded-sm">
      <div className="text-xs font-semibold text-stone-600 flex items-center gap-2">
        <Icon className={cn("w-3.5 h-3.5", color || "text-stone-600")} />
        {label}
      </div>
      <div className={cn("text-2xl font-bold tracking-tight tabular-nums", color || "text-stone-950")}>
        {value}
      </div>
    </div>
  );
}

function SortableHeader({ 
  label, 
  field, 
  currentField, 
  order, 
  onSort, 
  className,
  tooltip
}: { 
  label: string; 
  field: SortField; 
  currentField: SortField; 
  order: SortOrder; 
  onSort: (f: SortField) => void;
  className?: string;
  tooltip?: string;
}) {
  const isActive = currentField === field;

  return (
    <TableHead 
      className={cn("p-0", className)}
      aria-sort={isActive ? (order === "asc" ? "ascending" : "descending") : "none"}
    >
      <Tooltip>
        <TooltipTrigger>
          <button
            type="button"
            onClick={() => onSort(field)}
            aria-label={`Sort table by ${label}`}
            className={cn(
              "flex items-center gap-2 w-full h-full px-4 py-2 font-black text-sm text-stone-600 hover:bg-stone-200 focus-visible:bg-stone-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-500 transition-colors group/btn cursor-pointer text-left border-none bg-transparent rounded-none",
              isActive && "text-black bg-stone-200/50",
              className?.includes("text-right") && "justify-end text-right",
              className?.includes("text-center") && "justify-center text-center"
            )}
          >
            {label}
            <div className="flex items-center gap-0.5">
              {isActive && (
                order === "asc" ? <ArrowUp className="w-4 h-4 text-black" /> : <ArrowDown className="w-4 h-4 text-black" />
              )}
            </div>
          </button>
        </TooltipTrigger>
        {tooltip && (
          <TooltipContent className="bg-stone-900 text-white border-stone-800 text-sm p-4 shadow-2xl max-w-xs font-bold leading-relaxed px-4 py-3">
             <div className="flex items-start gap-2">
                <Info className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                {tooltip}
             </div>
          </TooltipContent>
        )}
      </Tooltip>
    </TableHead>
  );
}
